import { useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { SearchableSelect } from '../../../components/SearchableSelect'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { ApprovalStep } from '@cmms/api-client'
import type { User, WorkOrder } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useAuthStore } from '../../../store/authStore'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { UserAvatar } from '../../../components/UserAvatar'
import { DigitalSigner } from '../../../components/DigitalSigner'

const STEP_COLORS = {
  pending: '#D97706',
  approved: '#059669',
  rejected: '#DC2626',
}

interface Props { workOrder: WorkOrder; editable: boolean }

export function ApprovalChain({ workOrder, editable }: Props) {
  const isTask = workOrder.kind === 'task'
  const client = isTask ? api.tasks : api.workOrders
  const qc = useQueryClient()
  const me = useAuthStore((s) => s.user)
  const { formatDate } = useOrgSettings()
  const [addOpen, setAddOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectNotes, setRejectNotes] = useState('')
  const [approveOpen, setApproveOpen] = useState(false)
  const [approveNotes, setApproveNotes] = useState('')
  // Pending notes carried across the signature step — once the user
  // signs, we call acknowledge with both signature_id and these notes.
  const [signerOpen, setSignerOpen] = useState(false)
  const [signerBusy, setSignerBusy] = useState(false)
  const [pendingNotes, setPendingNotes] = useState<string | undefined>(undefined)

  const stepsQ = useQuery({
    queryKey: ['approval-steps', workOrder.id],
    queryFn: () => client.listApprovalSteps(workOrder.id),
  })
  const steps: ApprovalStep[] = Array.isArray(stepsQ.data) ? stepsQ.data : []

  const approvedCount = steps.filter(s => s.status === 'approved').length
  const currentStep = steps.find(s => s.status === 'pending')
  const isMyTurn = currentStep?.approver_id === me?.id
  const allApproved = steps.length > 0 && steps.every(s => s.status === 'approved')
  const hasRejection = steps.some(s => s.status === 'rejected')

  const acknowledgeM = useMutation({
    mutationFn: (body?: { notes?: string; signature_id?: string }) =>
      client.acknowledge(workOrder.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-steps', workOrder.id] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      qc.invalidateQueries({ queryKey: ['work-order', workOrder.id] })
      toast.success(steps.length > 0 && !allApproved ? 'Step approved' : (isTask ? 'Task completed' : 'Work order resolved'))
      setApproveOpen(false)
      setApproveNotes('')
      setPendingNotes(undefined)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  // Mid-flow: either fire acknowledge directly, or hand off to the
  // signature dialog when THIS WO requires one. The signature dialog
  // then echoes back into runAcknowledge with the signature_id.
  function runAcknowledge(notes?: string) {
    if (workOrder.require_acknowledge_signature) {
      setPendingNotes(notes)
      setSignerOpen(true)
      return
    }
    acknowledgeM.mutate(notes ? { notes } : undefined)
  }

  function handleApprove() {
    // If the workflow that created this step opted into require_comment,
    // open the comment dialog instead of approving immediately. Otherwise
    // approve straight through.
    if (currentStep?.require_comment) {
      setApproveNotes('')
      setApproveOpen(true)
    } else {
      runAcknowledge(undefined)
    }
  }

  async function handleSignatureConfirm(blob: Blob) {
    setSignerBusy(true)
    try {
      const file = new File([blob], 'signature.png', { type: 'image/png' })
      const sig = await client.uploadSignature(workOrder.id, file, 'acknowledge')
      await acknowledgeM.mutateAsync({
        signature_id: sig.id,
        ...(pendingNotes ? { notes: pendingNotes } : {}),
      })
      setSignerOpen(false)
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to submit signature')
    } finally {
      setSignerBusy(false)
    }
  }

  const rejectM = useMutation({
    mutationFn: (notes: string) => client.reject(workOrder.id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-steps', workOrder.id] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
      qc.invalidateQueries({ queryKey: ['work-order', workOrder.id] })
      toast.success('Rejected — sent back for rework')
      setRejectOpen(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const removeM = useMutation({
    mutationFn: (stepId: string) => client.removeApprovalStep(workOrder.id, stepId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval-steps', workOrder.id] }); toast.success('Step removed') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  if (steps.length === 0 && !editable) return null

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
          Approval chain {steps.length > 0 && `(${approvedCount}/${steps.length})`}
        </Typography>
        {editable && workOrder.status !== 'resolved' && (
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setAddOpen(true)}
            sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>Add approver</Button>
        )}
      </Stack>

      {/* Progress bar */}
      {steps.length > 0 && (
        <Box sx={{ mb: 2, height: 4, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', borderRadius: 99, transition: 'width 0.5s ease',
            width: `${(approvedCount / steps.length) * 100}%`,
            bgcolor: hasRejection ? '#DC2626' : allApproved ? '#059669' : '#D97706' }} />
        </Box>
      )}

      {/* Steps */}
      {steps.length > 0 ? (
        <Stack spacing={0}>
          {steps.map((step, i) => {
            const color = STEP_COLORS[step.status]
            const isLast = i === steps.length - 1
            const isCurrent = step.id === currentStep?.id

            return (
              <Stack key={step.id} direction="row" spacing={1.5} sx={{ position: 'relative' }}>
                {/* Timeline line + dot */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${color}18`, border: isCurrent ? '2px solid' : 'none', borderColor: color,
                  }}>
                    {step.status === 'approved' && <CheckCircleIcon sx={{ fontSize: 16, color }} />}
                    {step.status === 'rejected' && <CancelIcon sx={{ fontSize: 16, color }} />}
                    {step.status === 'pending' && <ScheduleOutlinedIcon sx={{ fontSize: 14, color }} />}
                  </Box>
                  {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: steps[i + 1]?.status === 'approved' ? '#059669' : 'divider', my: 0.25 }} />}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, pb: isLast ? 0 : 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <UserAvatar name={step.approver_name} size={24} sx={{ bgcolor: color }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{step.approver_name}</Typography>
                        <Chip label={`Step ${step.step_order}`} size="small"
                          sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
                        {isCurrent && workOrder.status === 'closed' && (
                          <Chip label="Current" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#FFFBEB', color: '#D97706' }} />
                        )}
                      </Stack>
                      {step.acted_at && (
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {step.status === 'approved' ? 'Approved' : 'Rejected'} · {formatDate(step.acted_at)}
                        </Typography>
                      )}
                      {step.status === 'pending' && step.due_at && (() => {
                        const remaining = formatRemaining(step.due_at)
                        const overdue = remaining.overdue
                        return (
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4 }}>
                            <ScheduleOutlinedIcon sx={{
                              fontSize: 12,
                              color: overdue ? '#DC2626' : '#D97706'
                            }} />
                            <Typography sx={{
                              fontSize: 11.5, fontWeight: 600,
                              color: overdue ? '#DC2626' : '#D97706'
                            }}>
                              {overdue ? `Overdue by ${remaining.label}` : `${remaining.label} left to respond`}
                            </Typography>
                            {step.escalate_to_name && (
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                                · auto-escalates to {step.escalate_to_name}
                              </Typography>
                            )}
                          </Stack>
                        )
                      })()}
                      {step.escalated_at && step.status === 'pending' && (
                        <Typography sx={{ fontSize: 11, color: '#DC2626', mt: 0.25, fontWeight: 600 }}>
                          Auto-escalated · originally went to a different approver
                        </Typography>
                      )}
                      {step.require_comment && step.status === 'pending' && isCurrent && isMyTurn && (
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
                          A comment is required to approve this step.
                        </Typography>
                      )}
                      {step.notes && (
                        <Typography sx={{
                          fontSize: 11.5, mt: 0.25, fontStyle: 'italic',
                          color: step.status === 'approved' ? 'text.secondary' : '#DC2626',
                        }}>
                          "{step.notes}"
                        </Typography>
                      )}
                    </Box>
                    {editable && step.status === 'pending' && workOrder.status !== 'resolved' && (
                      <IconButton size="small" onClick={() => removeM.mutate(step.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )
          })}
        </Stack>
      ) : editable ? (
        <Box sx={{ py: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No approval chain configured</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>Add approvers to require sign-off before resolution</Typography>
        </Box>
      ) : null}

      {/* Action buttons for current approver */}
      {workOrder.status === 'closed' && isMyTurn && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" color="success" size="small" fullWidth
            disabled={acknowledgeM.isPending}
            onClick={handleApprove}
            sx={{ fontSize: 12.5, fontWeight: 600, borderRadius: 2 }}>
            {acknowledgeM.isPending ? 'Approving...' : 'Approve'}
          </Button>
          <Button variant="outlined" color="error" size="small" fullWidth
            onClick={() => { setRejectNotes(''); setRejectOpen(true) }}
            sx={{ fontSize: 12.5, fontWeight: 600, borderRadius: 2 }}>
            Reject
          </Button>
        </Stack>
      )}

      {/* Add approver dialog */}
      {addOpen && <AddApproverDialog workOrderId={workOrder.id} isTask={isTask} onClose={() => setAddOpen(false)} />}

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Reject & send back</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            This will send the {isTask ? 'task' : 'work order'} back to In Progress for rework.
          </Typography>
          <TextField label="Reason" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
            size="small" fullWidth multiline minRows={2} placeholder="What needs to be fixed?" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRejectOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={rejectM.isPending}
            onClick={() => rejectM.mutate(rejectNotes)}>
            {rejectM.isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve-with-comment dialog (when require_comment is true) */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>Approve with comment</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
            This step requires a comment before it can be approved.
          </Typography>
          <TextField label="Comment" value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)}
            size="small" fullWidth multiline minRows={2} placeholder="Reason / sign-off note…" autoFocus />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setApproveOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="success"
            disabled={acknowledgeM.isPending || approveNotes.trim().length === 0}
            onClick={() => runAcknowledge(approveNotes.trim())}>
            {acknowledgeM.isPending ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <DigitalSigner
        open={signerOpen}
        title="Sign to approve"
        subtitle={`Your signature will be recorded for this approval step.`}
        onClose={() => { if (!signerBusy) { setSignerOpen(false); setPendingNotes(undefined) } }}
        onConfirm={handleSignatureConfirm}
        busy={signerBusy || acknowledgeM.isPending}
      />
    </Box>
  )
}

function AddApproverDialog({ workOrderId, isTask, onClose }: { workOrderId: string; isTask: boolean; onClose: () => void }) {
  const client = isTask ? api.tasks : api.workOrders
  const qc = useQueryClient()
  const [userId, setUserId] = useState('')

  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 60_000 })
  const users: User[] = Array.isArray(usersQ.data) ? usersQ.data : []

  const addM = useMutation({
    mutationFn: () => client.addApprovalStep(workOrderId, { approver_id: userId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['approval-steps', workOrderId] }); toast.success('Approver added'); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add approver</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          Select who should approve this {isTask ? 'task' : 'work order'}. Approvers are notified in order.
        </Typography>
        <SearchableSelect
          value={userId}
          onChange={setUserId}
          options={users}
          getValue={(u) => u.id}
          getLabel={(u) => u.name}
          placeholder="Select a person"
          renderOption={(u) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <UserAvatar name={u.name} avatarUrl={(u as any).avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
              <Box>
                <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{(u as any).role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
              </Box>
            </Stack>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!userId || addM.isPending} onClick={() => addM.mutate()}>
          {addM.isPending ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Returns a human-readable countdown / overdue duration for the approval
 * step's `due_at`. Used by the SLA chip.
 */
function formatRemaining(dueAt: string): { label: string; overdue: boolean } {
  const due = new Date(dueAt).getTime()
  const diffMs = due - Date.now()
  const overdue = diffMs < 0
  const ms = Math.abs(diffMs)
  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days >= 1) return { overdue, label: `${days}d ${hours % 24}h` }
  if (hours >= 1) return { overdue, label: `${hours}h ${minutes % 60}m` }
  return { overdue, label: `${Math.max(minutes, 0)}m` }
}
